<template>
    <div class="space-y-6">
        <h1 class="text-lg font-bold">Custom Offers</h1>

        <div class="space-y-4 border-b-2 pb-6">
            <offer
                :amount="customOffer.full_outstanding_balance"
                :selected="form.offer === 'full_outstanding_balance'"
                @click="form.offer = 'full_outstanding_balance'" />
            <offer
                v-if="customOffer.discount_balance"
                :amount="customOffer.discount_balance"
                :total="customOffer.full_outstanding_balance"
                :selected="form.offer === 'discount_balance'" :discount="true"
                @click="form.offer = 'discount_balance'" />
        </div>

        <frequency
            :frequency="form.frequency"
            @select="changeFrequency" />

        <payment-amount
            :min="minAmount"
            :max="maxAmount"
            :amount="form.amount"
            :disabled="form.frequency === 'at-once'"
            @input="form.amount = $event"
            class="border-b-2 pb-6" />

        <payment-date
            :date="form.payment_dates[0]"
            :amount="form.amount"
            :first="true"
            :min="minFirstPaymentDate"
            :max="maxFirstPaymentDate"
            @input="firstPaymentDate"
            class="border-b-2 pb-6" />

        <payment-date
            v-if="form.frequency !== 'at-once'"
            :date="form.payment_dates[1]"
            :amount="secondPaymentAmount"
            :min="minSecondPaymentDate"
            :max="maxSecondPaymentDate"
            @input="secondPaymentDate"
            class="border-b-2 pb-6" />

        <payment-method
            :settings="$parent.$parent.settings"
            :billing-profile="billingProfile"
            :form="form"
            :error="error"
        />

        <div class="flex">
            <button @click="preview" :class="{
                    [`flex-1 rounded-lg text-white text-base font-medium bg-${$root.theme}-200 py-3 text-center`]: true,
                }">
                Continue
            </button>
        </div>
    </div>
</template>

<script>
import Offer from './Offers/Offer.vue'
import Frequency from './Offers/Frequency.vue'
import PaymentAmount from './Offers/PaymentAmount.vue'
import PaymentDate from './Offers/PaymentDate.vue'
import Moment from 'moment/moment'
import PaymentMethod from './Offers/PaymentMethod.vue'
import Mixins from '../../mixins'
import Vue from 'vue'

export default {
    mixins: [Mixins],

    components: { PaymentMethod, PaymentDate, Frequency, Offer, PaymentAmount },

    props: {
        billingProfile: Object,
        customOffer: Object,
    },

    created() {
        this.form.how_obtained_id = this.customOffer.how_obtained_id;
        this.form.offer = this.customOffer.payment_amount_type === 'discount_balance' ? 'discount_balance' : 'full_outstanding_balance';
        this.form.amount = this.customOffer.amount.toFixed(2);
        this.form.frequency = this.customOffer.frequency;

        let today = Moment(),
            firstPaymentDate = Moment(this.customOffer.start_date),
            secondPaymentDate = Moment(this.customOffer.second_recurring_date);

        if (! today.isAfter(firstPaymentDate)) {
            this.form.payment_dates[0] = firstPaymentDate.format('YYYY-MM-DD');
        } else {
            this.form.payment_dates[0] = today.format('YYYY-MM-DD');
        }

        if (! today.isAfter(secondPaymentDate)) {
            this.form.payment_dates[1] = this.customOffer.second_recurring_date;
        } else {
            if (this.form.frequency === 'monthly') {
                this.form.payment_dates[1] = Moment(this.customOffer.second_recurring_date).add(1, 'month').format('YYYY-MM-DD');
            } else {
                this.form.payment_dates[1] = Moment(this.customOffer.second_recurring_date).add(2, 'week').format('YYYY-MM-DD');
            }
        }
    },

    data() {
        return {
            error: null,
            form: {
                offer: 'full_outstanding_balance',
                'custom-offer': true,
                how_obtained_id: null,
                amount: 0,
                frequency: 'monthly',

                payment_date: null,
                payment_dates: [],

                type: 'card',
                first_name: '',
                last_name: '',
                address: '',
                city: '',
                state: '',
                billing_profile_id: null,

                routing_number: '',
                account_number: '',

                cc_number: '',
                card_exp: '',
                exp_month: '',
                exp_year: '',
                cvv: '',
                zip: '',
            }
        }
    },

    watch: {
        'form.offer'(offer, oldOffer) {
            if (this.form.amount < this.minAmount) {
                this.form.amount = this.minAmount.toFixed(2);
            }

            if (this.form.amount < this.minAmount) {
                this.setAmountToMinimum(this.form.frequency);
            }
        }
    },

    computed: {
        Moment() {
            return Moment
        },
        minAmount() {
            if (this.form.frequency === 'at-once') return this.customOffer[`${this.form.offer}`];

            if (this.form.frequency === 'end-of-month') return 0;

            return this.customOffer[`${this.form.frequency}_minimum_${this.form.offer}`];
        },
        maxAmount() {
            return this.customOffer[`${this.form.offer}`];
        },
        minFirstPaymentDate() {
            return Moment().format('YYYY-MM-DD');
        },
        maxFirstPaymentDate() {
            return Moment().add(15, 'day').format('YYYY-MM-DD');
        },
        minSecondPaymentDate() {
            return Moment(this.form.payment_dates[0]).add(1, 'day').format('YYYY-MM-DD');
        },
        maxSecondPaymentDate() {
            if (this.form.frequency === 'biweekly') {
                return Moment(this.form.payment_dates[0]).add(4, 'week').format('YYYY-MM-DD');
            } else if (this.form.frequency === 'monthly') {
                return Moment(this.form.payment_dates[0]).add(1, 'month').format('YYYY-MM-DD');
            }
        },
        secondPaymentAmount() {
            let totalAfterFirstPayment = this.customOffer[`${this.form.offer}`] - this.form.amount;

            if (totalAfterFirstPayment < this.form.amount) {
                return totalAfterFirstPayment;
            }

            return this.form.amount;
        }
    },

    methods: {
        setAmountToMinimum(frequency) {
            if (frequency === 'biweekly' || frequency === 'monthly') {
                this.form.amount = this.minAmount.toFixed(2);
            } else if (frequency === 'at-once') {
                this.form.amount = this.customOffer[`${this.form.offer}`].toFixed(2);
            }
        },

        changeFrequency(frequency) {
            this.form.frequency = frequency;

            this.setAmountToMinimum(frequency);

            if (this.form.frequency === 'biweekly') {
                Vue.set(this.form.payment_dates, 1, Moment(this.form.payment_dates[0]).add(2, 'week').format('YYYY-MM-DD'));
            } else if (this.form.frequency === 'monthly') {
                Vue.set(this.form.payment_dates, 1, Moment(this.form.payment_dates[0]).add(1, 'month').format('YYYY-MM-DD'));
            }
        },

        firstPaymentDate(firstPaymentDate) {
            Vue.set(this.form.payment_dates, 0, firstPaymentDate);

            let secondPaymentDate = null;

            if (this.form.frequency === 'monthly') {
                secondPaymentDate = Moment(this.form.payment_dates[0]).add(1, 'month').format('YYYY-MM-DD');
            } else {
                secondPaymentDate = Moment(this.form.payment_dates[0]).add(2, 'week').format('YYYY-MM-DD');
            }

            Vue.set(this.form.payment_dates, 1, secondPaymentDate);
        },

        secondPaymentDate(secondPaymentDate) {
            Vue.set(this.form.payment_dates, 1, secondPaymentDate);
        },

        preview() {
            if (
                this.form.amount < this.minAmount
                || this.form.amount > this.maxAmount
            ) {
                console.log('amount');

                return;
            }

            if (
                this.form.payment_dates[0] < this.minFirstPaymentDate
                || this.form.payment_dates[0] > this.maxFirstPaymentDate
            ) {
                console.log('first payment date');

                return;
            }

            if (
                this.form.frequency !== 'at-once'
                && (
                    this.form.payment_dates[1] < this.minSecondPaymentDate
                    || this.form.payment_dates[1] > this.maxSecondPaymentDate
                )
            ) {
                console.log('second payment date');

                return;
            }

            if (this.form.type === 'card' && ! this.checkCardType(this.form)) {
                console.log('card');

                return;
            }

            Vue.set(this.form, 'payment_date', this.form.payment_dates[0]);

            this.$emit('continue', this.form);
        }
    }
}
</script>
